import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { authApi } from 'src/api/auth/authService';
import { Issuer } from 'src/utils/auth';
import { AuthContext, initialState } from './auth-context';

import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import SessionManager from 'src/components/session-manager';
import { connectSocket, disconnectSocket } from 'src/utils/socket';
import { getAccessToken, setAccessToken, clearAccessToken } from 'src/utils/token-store';
import { registrarSuscripcionPush, eliminarSuscripcionPush } from 'src/utils/push';

var ActionType;
(function (ActionType) {
  ActionType['INITIALIZE'] = 'INITIALIZE';
  ActionType['SIGN_IN'] = 'SIGN_IN';
  ActionType['SIGN_UP'] = 'SIGN_UP';
  ActionType['SIGN_OUT'] = 'SIGN_OUT';
  ActionType['REFRESH_SESSION'] = 'REFRESH_SESSION';
})(ActionType || (ActionType = {}));

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user } = action.payload;

    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
    };
  },
  SIGN_IN: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
  SIGN_UP: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
  SIGN_OUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: {
      role: null,
      sub_role: null,
      is_super: false,
      permissions: [],
    },
  }),
  REFRESH_SESSION: (state, action) => {
    const { user } = action.payload;
    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
};

const reducer = (state, action) =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

export const AuthProvider = (props) => {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialState);
  const [showModal, setShowModal] = useState(false);
  const [counter, setCounter] = useState(20);
  const navigate = useNavigate();
  const countdownRef = useRef(null);

  const getTokenExpirationTime = () => {
    const token = getAccessToken();
    if (!token) return null;

    const decodedToken = jwtDecode(token);
    return decodedToken.exp * 1000;
  };

  const refreshSession = useCallback(async () => {
    try {
      const { token } = await authApi.refreshToken();
      setAccessToken(token);
      connectSocket(token);
      registrarSuscripcionPush({ pedirPermiso: false }).catch(() => {});

      const user = await authApi.me();
      dispatch({
        type: ActionType.REFRESH_SESSION,
        payload: {
          user,
        },
      });
    } catch (err) {
      setShowModal(false);
      console.error('Error al refrescar la sesión:', err);
    }
  }, [dispatch]);

  const initialize = useCallback(async () => {
    try {
      let token = null;
      try {
        const res = await authApi.refreshToken();
        token = res.token;
      } catch (err) {
        token = null;
      }

      if (token) {
        setAccessToken(token);
        connectSocket(token);
        registrarSuscripcionPush({ pedirPermiso: false }).catch(() => {});
        const user = await authApi.me();
        dispatch({
          type: ActionType.INITIALIZE,
          payload: { isAuthenticated: true, user },
        });
      } else {
        clearAccessToken();
        dispatch({
          type: ActionType.INITIALIZE,
          payload: { isAuthenticated: false, user: null },
        });
      }
    } catch (err) {
      console.error(err);
      clearAccessToken();
      dispatch({
        type: ActionType.INITIALIZE,
        payload: { isAuthenticated: false, user: null },
      });
    }
  }, [dispatch]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  useEffect(() => {
    if (showModal) {
      countdownRef.current = setInterval(() => {
        setCounter((prevCounter) => {
          if (prevCounter <= 1) {
            clearInterval(countdownRef.current);
            setShowModal(false);
            signOut();
            navigate('/');
            return 0;
          }
          return prevCounter - 1;
        });
      }, 1000);
    } else {
      clearInterval(countdownRef.current);
    }

    return () => clearInterval(countdownRef.current);
  }, [showModal, navigate]);

  const signIn = useCallback(
    async (email, password) => {
      const { accessToken } = await authApi.signIn({ email, password });
      setAccessToken(accessToken);
      connectSocket(accessToken);
      registrarSuscripcionPush().catch(() => {});

      const user = await authApi.me();
      dispatch({
        type: ActionType.SIGN_IN,
        payload: {
          user,
        },
      });
      return user;
    },
    [dispatch]
  );

  const signUp = useCallback(
    async (dataUser) => {
      await authApi.signUp({ ...dataUser });

      dispatch({
        type: ActionType.SIGN_UP,
        payload: {
          user: null,
        },
      });
    },
    [dispatch]
  );

  const signOut = useCallback(async () => {
    eliminarSuscripcionPush().catch(() => {});
    try {
      await authApi.logout();
    } catch (err) {
      console.error('Error al cerrar sesión en el servidor:', err);
    }
    clearAccessToken();
    disconnectSocket();
    dispatch({ type: ActionType.SIGN_OUT });
  }, [dispatch]);

  const extendSession = useCallback(() => {
    refreshSession();
    setShowModal(false);
    setCounter(20);
  }, [refreshSession]);

  useEffect(() => {
    const isSessionActive = () => {
      const currentTime = Date.now();
      const expirationTime = getTokenExpirationTime();
      return expirationTime > currentTime;
    };

    if (!isSessionActive()) {
      return;
    }

    const handleUserActivity = () => {
      if (!showModal) {
        setCounter(20);

        const currentTime = Date.now();
        const expirationTime = getTokenExpirationTime();
        const timeRemaining = expirationTime - currentTime;

        if (timeRemaining <= 1000 * 20 && timeRemaining > 0) {
          refreshSession();
        }
      }
    };

    window.addEventListener('mousemove', handleUserActivity);
    window.addEventListener('keypress', handleUserActivity);

    const checkInactivity = setInterval(() => {
      const currentTime = Date.now();
      const expirationTime = getTokenExpirationTime();
      const timeRemaining = expirationTime - currentTime;

      if (timeRemaining <= 1000 * 20 && !showModal && timeRemaining > 0) {
        setShowModal(true);
        setCounter(20);
      }

      if (timeRemaining <= 0) {
        setShowModal(false);
        signOut();
        navigate('/');
        clearInterval(checkInactivity);
        window.removeEventListener('mousemove', handleUserActivity);
        window.removeEventListener('keypress', handleUserActivity);
      }
    }, 1000);

    return () => {
      clearInterval(checkInactivity);
      window.removeEventListener('mousemove', handleUserActivity);
      window.removeEventListener('keypress', handleUserActivity);
    };
  }, [refreshSession, showModal, navigate, signOut, getTokenExpirationTime]);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        issuer: Issuer.JWT,
        initialize,
        signIn,
        signUp,
        signOut,
        refreshSession,
      }}
    >
      {children}

      <SessionManager
        showModal={showModal}
        signOut={signOut}
        counter={counter}
        extendSession={extendSession}
      />
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
